import React, { useState } from "react";
import { Plus, Trash2, Loader2, Send, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const SERVICE_TYPES = [
  "Cleaning",
  "Plumbing",
  "Electrical",
  "HVAC",
  "Roofing",
  "Landscaping",
  "Pest Control",
  "Carpet Cleaning",
  "Window Cleaning",
  "Handyman",
  "Painting",
  "Other",
];

const RESPONSE_TIMES = [
  "Within 1 business hour",
  "Within 2 business hours",
  "Within 4 business hours",
  "Within 1 business day",
  "Within 2 business days",
  "Same day if booked before 2pm",
  "Within 24 hours",
  "Within 48 hours",
];
const RESPONSE_TIME_CUSTOM = "Custom — I will specify below";

export default function GetStarted() {
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    contact_email: "",
    website_url: "",
    service_type: "Cleaning",
    notification_email: "",
    response_time: "",
    response_time_custom: "",
    pricing_variables: "",
    brand_color: "#06B6D4",
    logo_url: "",
    widget_tagline: "",
    notes: "",
  });
  const [services, setServices] = useState([
    { name: "", base_price: "" },
    { name: "", base_price: "" },
  ]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const addService = () => setServices((s) => [...s, { name: "", base_price: "" }]);
  const removeService = (i) => setServices((s) => s.filter((_, idx) => idx !== i));
  const updateService = (i, key, val) =>
    setServices((s) => s.map((row, idx) => (idx === i ? { ...row, [key]: val } : row)));

  const onSubmit = async (e) => {
    e.preventDefault();
    const required = ["business_name", "contact_name", "contact_email", "service_type"];
    for (const k of required) {
      if (!form[k]) {
        toast.error("Please complete all required fields.");
        return;
      }
    }
    const cleanServices = services
      .map((s) => ({ name: s.name.trim(), base_price: s.base_price.toString().trim() }))
      .filter((s) => s.name);
    if (cleanServices.length === 0) {
      toast.error("Add at least one service.");
      return;
    }
    setSubmitting(true);
    // Resolve the response_time into a single string the backend can use.
    // If "Custom" is selected, use the user-typed text; otherwise use the dropdown value.
    const isCustom = form.response_time === RESPONSE_TIME_CUSTOM;
    const resolvedResponseTime = isCustom
      ? form.response_time_custom.trim()
      : form.response_time;
    // Strip the transient response_time_custom — backend doesn't expect it
    const { response_time_custom: _custom, ...formForSubmit } = form;
    try {
      await api.post("/forms/onboarding", {
        ...formForSubmit,
        response_time: resolvedResponseTime,
        services: cleanServices,
      });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="relative min-h-[70vh] bg-[#0A1628] text-[#F8FAFC] py-20">
        <div className="absolute inset-0 so-grid opacity-25 pointer-events-none" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] so-glow-cyan pointer-events-none" />
        <div className="relative max-w-xl mx-auto px-6">
          <div className="so-card rounded-2xl p-10 text-center" data-testid="get-started-success">
            <div className="font-display text-3xl font-extrabold tracking-tight">
              Thanks — we will have your widget ready within 2 business days.
            </div>
            <p className="mt-4 text-[#94A3B8] text-sm leading-relaxed">
              Keep an eye on your inbox at{" "}
              <span className="text-[#06B6D4]">{form.contact_email}</span>.
            </p>
            <a
              href="/"
              className="btn-blue inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-medium mt-8"
            >
              Back to homepage <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#0A1628] text-[#F8FAFC] py-16 sm:py-20">
      <div className="absolute inset-0 so-grid opacity-20 pointer-events-none" />
      <div className="absolute top-20 right-0 w-[420px] h-[420px] so-glow-cyan pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[420px] h-[420px] so-glow-blue pointer-events-none" />

      <div className="relative max-w-3xl mx-auto px-6">
        <div className="text-center mb-10">
          <div className="uppercase text-xs tracking-widest text-[#06B6D4] font-mono">Onboarding</div>
          <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight">
            Let's build your quoting widget.
          </h1>
          <p className="mt-4 text-[#94A3B8] text-sm sm:text-base leading-relaxed max-w-xl mx-auto">
            Fill in your details below — we'll configure your fully-branded
            widget and email you when it's ready (usually within 2 business days).
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="get-started-form"
          className="so-card rounded-2xl p-6 sm:p-8 space-y-7"
        >
          {/* Business */}
          <Section title="About your business">
            <Field label="Business name *">
              <input data-testid="gs-business" value={form.business_name} onChange={set("business_name")} className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </Field>
            <Field label="Contact name *">
              <input data-testid="gs-contact-name" value={form.contact_name} onChange={set("contact_name")} className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </Field>
            <Field label="Contact email *">
              <input data-testid="gs-contact-email" type="email" value={form.contact_email} onChange={set("contact_email")} className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </Field>
            <Field label="Website URL">
              <input data-testid="gs-website" placeholder="https://yourbusiness.com" value={form.website_url} onChange={set("website_url")} className="so-input w-full rounded-lg px-4 py-3 text-sm" />
            </Field>
            <Field label="Service type *">
              <select data-testid="gs-service-type" value={form.service_type} onChange={set("service_type")} className="so-input w-full rounded-lg px-4 py-3 text-sm appearance-none">
                {SERVICE_TYPES.map((s) => (
                  <option key={s} value={s} className="bg-[#0A1628] text-[#F8FAFC]">{s}</option>
                ))}
              </select>
            </Field>
          </Section>

          {/* Services & pricing */}
          <Section title="Services & base prices">
            <p className="text-xs text-[#94A3B8] -mt-2 mb-3 leading-relaxed">
              Add the core services you offer and their starting prices. You can refine these later inside your account.
            </p>
            <div className="space-y-3">
              {services.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`gs-service-row-${i}`}>
                  <input
                    data-testid={`gs-service-name-${i}`}
                    placeholder="e.g. 3-bedroom house clean"
                    value={row.name}
                    onChange={(e) => updateService(i, "name", e.target.value)}
                    className="so-input col-span-7 rounded-lg px-3 py-2.5 text-sm"
                  />
                  <input
                    data-testid={`gs-service-price-${i}`}
                    placeholder="$ price"
                    value={row.base_price}
                    onChange={(e) => updateService(i, "base_price", e.target.value)}
                    className="so-input col-span-4 rounded-lg px-3 py-2.5 text-sm"
                  />
                  <button
                    type="button"
                    onClick={() => removeService(i)}
                    aria-label="Remove service"
                    data-testid={`gs-service-remove-${i}`}
                    className="col-span-1 text-[#94A3B8] hover:text-[#EC4899] transition-colors flex justify-center"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addService}
              data-testid="gs-add-service"
              className="mt-3 inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-mono text-[#06B6D4] hover:text-[#F8FAFC]"
            >
              <Plus size={14} /> Add another service
            </button>

            <Field label="Pricing variables (e.g. surcharges, distance, after-hours)">
              <textarea
                data-testid="gs-pricing-variables"
                rows={3}
                value={form.pricing_variables}
                onChange={set("pricing_variables")}
                className="so-input w-full rounded-lg px-4 py-3 text-sm resize-none"
                placeholder="e.g. +$50 same-day, +$30 weekend, +$0.80/km over 20km"
              />
            </Field>
          </Section>

          {/* Branding & notifications */}
          <Section title="Branding & notifications">
            <Field label="Notification email (where leads land)">
              <input data-testid="gs-notification" type="email" value={form.notification_email} onChange={set("notification_email")} className="so-input w-full rounded-lg px-4 py-3 text-sm" placeholder="leads@yourbusiness.com" />
            </Field>
            <Field label="How quickly will you contact customers after they accept a quote?">
              <select
                data-testid="gs-response-time"
                value={form.response_time}
                onChange={set("response_time")}
                className="so-input w-full rounded-lg px-4 py-3 text-sm bg-[#091324] appearance-none"
              >
                <option value="">Select an option (optional — defaults to 1 business day)</option>
                {RESPONSE_TIMES.map((rt) => (
                  <option key={rt} value={rt}>{rt}</option>
                ))}
                <option value={RESPONSE_TIME_CUSTOM}>{RESPONSE_TIME_CUSTOM}</option>
              </select>
              {form.response_time === RESPONSE_TIME_CUSTOM && (
                <input
                  data-testid="gs-response-time-custom"
                  type="text"
                  value={form.response_time_custom}
                  onChange={set("response_time_custom")}
                  placeholder="e.g. Within 4 business hours during weekdays"
                  className="so-input w-full rounded-lg px-4 py-3 text-sm mt-3"
                />
              )}
              <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed">
                This is the commitment shown to your customers after they accept a quote — e.g. Pristine Property Services will be in touch within 4 business hours to confirm your booking.
              </p>
            </Field>
            <Field label="Brand colour">
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  data-testid="gs-color"
                  value={form.brand_color}
                  onChange={set("brand_color")}
                  className="w-12 h-10 rounded-md bg-[#091324] border border-white/10 cursor-pointer p-1"
                />
                <input
                  type="text"
                  value={form.brand_color}
                  onChange={set("brand_color")}
                  className="so-input flex-1 rounded-lg px-4 py-2.5 text-sm font-mono"
                />
              </div>
            </Field>
            <Field label="Business logo URL (optional)">
              <input
                data-testid="gs-logo-url"
                type="url"
                value={form.logo_url}
                onChange={set("logo_url")}
                placeholder="https://yourbusiness.com/logo.png"
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
              />
              <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed">
                Paste a link to your logo from your existing website — right-click your logo and copy image address.
              </p>
            </Field>
            <Field label="Widget tagline (optional)">
              <input
                data-testid="gs-widget-tagline"
                type="text"
                value={form.widget_tagline}
                onChange={set("widget_tagline")}
                placeholder="e.g. Get an instant quote in 60 seconds"
                maxLength={80}
                className="so-input w-full rounded-lg px-4 py-3 text-sm"
              />
              <p className="mt-2 text-xs text-[#94A3B8] leading-relaxed">
                This short line appears beneath your business name on the quoting widget. Keep it under 50 characters.
              </p>
            </Field>
            <Field label="Anything else we should know?">
              <textarea data-testid="gs-notes" rows={4} value={form.notes} onChange={set("notes")} className="so-input w-full rounded-lg px-4 py-3 text-sm resize-none" />
            </Field>
          </Section>

          <button
            type="submit"
            disabled={submitting}
            data-testid="gs-submit"
            className="btn-pink w-full inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold disabled:opacity-60"
          >
            {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Submit & start onboarding
          </button>
          <p className="text-[11px] text-[#94A3B8] text-center font-mono">
            We'll have your widget ready within 2 business days.
          </p>
        </form>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="font-display text-lg font-bold tracking-tight mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-widest text-[#94A3B8] mb-2 font-mono">
        {label}
      </label>
      {children}
    </div>
  );
}
